/** @module pairingAction */

import config from '@bedrock-rdk/config';
import { parentalControlService, tokenService } from '@bedrock/fido-sdk';
import { storage } from '@bedrock-rdk/utils';
import { getCurrentSubscriptions, isPaired } from './pairing.utils';
import { getCookies, getUid } from '../user/user.selectors';
import { ERROR_LEVELS, ERROR_TYPES } from '../error/error.constants';
import { errorAction } from '../error/error.actions';
import { fetchProfileInfos, setUserUid } from '../user/user.actions';
import { getMultiProfilesGateAlwaysDisplayed, getPremiumFeature } from '../features/features.selectors';
import { sendEvent, setAttributes, trackGDPRConsent } from '../analytics/analytics.utils';
import { getErrorLevel } from '../error/error.utils';
import { landingShown } from '../routing/routing.actions';
import { refreshCustomizedConfig } from '../app/app.config.actions';
import { PERSISTENT } from '../app/app.constants';
import { fetchOneTimePasscode, fetchUser, getProviderPairingStatus } from './customer/default/pairingProvider.actions';
import { resetSearch } from '../search/search.reset.actions';
import { setCurrentSectionCode } from '../layout/layout.section.actions';
import { logout as logoutBase } from './unpair.actions';

export { logoutSuccess, logoutFailure, PAIRING_LOGOUT_SUCCESS, PAIRING_LOGOUT_FAILURE } from './unpair.actions';

export const PAIRING_SET_POLLING_INTERVAL = 'pairing/SET_POLLING_INTERVAL';
export const PAIRING_INCREMENT_POLLING_INTERVAL = 'pairing/PAIRING_INCREMENT_POLLING_INTERVAL';
export const PAIRING_FETCH_TOKEN_SUCCESS = 'pairing/FETCH_TOKEN_SUCCESS';
export const PAIRING_CLEAR_TOKEN = 'pairing/PAIRING_CLEAR_TOKEN';
export const PAIRING_FETCH_TOKEN_FAILURE = 'pairing/FETCH_TOKEN_FAILURE';
export const PAIRING_FETCH_STATUS_FAILURE = 'pairing/FETCH_STATUS_FAILURE';
export const PAIRING_FETCH_STATUS_SUCCESS = 'pairing/FETCH_STATUS_SUCCESS';
export const PAIRING_FETCH_USER_SUCCESS = 'pairing/FETCH_USER_SUCCESS';
export const PAIRING_FETCH_USER_FAILURE = 'pairing/FETCH_USER_FAILURE';
export const SET_SUBSCRIPTIONS = 'pairing/SET_SUBSCRIPTIONS';
export const WELCOME_CONTINUE_BUTTON = 'pairing/WELCOME_CONTINUE_BUTTON';
export const TOKEN_BACK_BUTTON = 'pairing/TOKEN_BACK_BUTTON';
export const SUBSCRIBE_BACK_BUTTON = 'pairing/SUBSCRIBE_BACK_BUTTON';
export const LANDING_SUBSCRIBE_BUTTON = 'pairing/LANDING_SUBSCRIBE_BUTTON';
export const LANDING_CONNECT_BUTTON = 'pairing/LANDING_CONNECT_BUTTON';
export const RETRY_CONNECT_BUTTON_FOCUS_NAME = 'pairing/RETRY_CONNECT_BUTTON_FOCUS_NAME';
export const PAIRING_FETCH_USER_CATALOG_ACCESS_SUCCESS = 'pairing/PAIRING_FETCH_USER_CATALOG_ACCESS_SUCCESS';
export const PAIRING_FETCH_USER_CATALOG_ACCESS_FAILURE = 'pairing/PAIRING_FETCH_USER_CATALOG_ACCESS_FAILURE';
export const PAIRING_FETCH_FREEMIUM_SERVICES_SUCCESS = 'pairing/PAIRING_FETCH_FREEMIUM_SERVICES_SUCCESS';
export const PAIRING_FETCH_FREEMIUM_SERVICES_FAILURE = 'pairing/PAIRING_FETCH_FREEMIUM_SERVICES_FAILURE';
export const PAIRING_SET_PARENTAL_LOCK_STATUS = 'pairing/SET_PARENTAL_LOCK_STATUS';
export const PAIRING_SET_UNPAIRED_ENTERING = 'pairing/PAIRING_SET_UNPAIRED_ENTERING';
export const PAIRING_CLEAR_UNPAIRED_ENTERING = 'pairing/PAIRING_CLEAR_UNPAIRED_ENTERING';
export const RENEW_TOKEN_SUCCESS = 'token/RENEW_TOKEN_SUCCESS';
export const RENEW_TOKEN_FAILURE = 'token/RENEW_TOKEN_FAILURE';
export const CLEAR_USER_PROFILE = 'token/CLEAR_USER_PROFILE';
export const SELECT_USER_PROFILE = 'token/SELECT_USER_PROFILE';

export const setUnpairedEntering = () => ({
  type: PAIRING_SET_UNPAIRED_ENTERING,
});

export const clearUnpairedEntering = () => ({
  type: PAIRING_CLEAR_UNPAIRED_ENTERING,
});

export const fetchTokenSuccess = (token, verificationURI) => ({
  type: PAIRING_FETCH_TOKEN_SUCCESS,
  token,
  verificationURI,
});

export const fetchTokenFailure = () => ({
  type: PAIRING_FETCH_TOKEN_FAILURE,
});

export const fetchStatusSuccess = status => ({ type: PAIRING_FETCH_STATUS_SUCCESS, status });

export const fetchStatusFailure = () => ({ type: PAIRING_FETCH_STATUS_FAILURE });

export const setPollingInterval = pollingInterval => ({
  type: PAIRING_SET_POLLING_INTERVAL,
  pollingInterval,
});

export const incrementPollingInterval = (value = 5000) => ({ type: PAIRING_INCREMENT_POLLING_INTERVAL, value });

export const renewTokenSuccess = profileUid => ({
  type: RENEW_TOKEN_SUCCESS,
  profileUid,
});

export const renewTokenFailure = error => ({
  type: RENEW_TOKEN_FAILURE,
  error,
});

export const setSubscriptions = subscriptions => ({
  type: SET_SUBSCRIPTIONS,
  subscriptions,
});

export const selectUserProfile = profileUid => ({
  type: SELECT_USER_PROFILE,
  profileUid,
});

export const clearUserProfile = () => ({
  type: CLEAR_USER_PROFILE,
});

export const clearPairingToken = () => ({
  type: PAIRING_CLEAR_TOKEN,
});

export const fetchToken = () => async dispatch => {
  try {
    sendEvent({ key: 'accountPairing' });
    const { token, verificationURI } = await dispatch(fetchOneTimePasscode());
    if (config.pairing && config.pairing.pairingPollInterval) {
      dispatch(setPollingInterval(config.pairing.pairingPollInterval));
    }
    dispatch(fetchTokenSuccess(token, verificationURI));
  } catch (error) {
    sendEvent({ key: 'accountPairing' });
    dispatch(fetchTokenFailure());
    dispatch(errorAction(ERROR_LEVELS.FATAL, ERROR_TYPES.PAIRING, error.message));
  }
};

export const fetchUserSuccess = user => async (dispatch, getState) => {
  try {
    const state = getState();
    const cookieConsents = getCookies(state);

    tokenService.resetUserAndToken();
    tokenService.setUser({ UID: user.uid });
    trackGDPRConsent(cookieConsents);

    // There is no need to display the landing page, user successfully signed in
    dispatch(landingShown());
    dispatch(clearUnpairedEntering());
    dispatch({ type: PAIRING_FETCH_USER_SUCCESS, user });
    dispatch(setUserUid(user.uid));

    // Analytics
    setAttributes({ authentified: 'yes', userStatus: 'connected' });
    sendEvent({ key: 'pairingSuccess' });

    // Restore profile if it was previously selected
    const shouldDisplayProfilesGate = getMultiProfilesGateAlwaysDisplayed(state);
    // NOTE: Since dev mode utilizes hot reload it's annoying to hit the profile selector each time we need to debug something.
    // If you need to disable this behavior, run an app in STAGING/PRODUCTION environment.
    // Also don't forget to clear "profileUid" cookie.
    if (!shouldDisplayProfilesGate) {
      const profileUid = storage.getItem(`${user.uid}.${PERSISTENT.PROFILE_UID_KEY}`) || null;

      if (profileUid) {
        tokenService.setProfile({ profileUid });
        dispatch(selectUserProfile(profileUid));
        dispatch(setCurrentSectionCode(storage.getItem(`${user.uid}.${PERSISTENT.PROFILE_SECTION_KEY}`)));
        dispatch(fetchProfileInfos(profileUid));
        dispatch(renewTokenSuccess(profileUid));
      } else {
        tokenService.setProfile({ profileUid: null });
      }
    }
  } catch (error) {
    dispatch({ type: PAIRING_FETCH_USER_FAILURE, error });
    dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.PAIRING, error.message));
    sendEvent({ key: 'pairingFail' });
  }
};

export const setParentalControl = parentalControlStatus => ({
  type: PAIRING_SET_PARENTAL_LOCK_STATUS,
  parentalControlStatus,
});

export const fetchParentalLockStatus = () => async (dispatch, getState) => {
  const userId = getUid(getState());
  try {
    const { maxCsaId } = await parentalControlService.fetchParentalRestriction({ uid: userId });
    dispatch(setParentalControl(maxCsaId));
  } catch (e) {
    dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.PAIRING, e.message));
  }
};

export const fetchUserSubscription = () => async (dispatch, getState) => {
  const userId = getUid(getState());
  try {
    const userSubscriptions = await getCurrentSubscriptions(userId);
    dispatch(setSubscriptions(userSubscriptions));
  } catch (error) {
    dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.PAIRING, error.message));
  }
};

export const fetchUserInformation = () => (dispatch, getState) => {
  const isPremiumEnabled = getPremiumFeature(getState());
  if (!isPremiumEnabled) {
    return Promise.resolve();
  }

  return Promise.all([dispatch(fetchUserSubscription()), dispatch(fetchParentalLockStatus())]);
};

export const getCurrentPairedUser =
  (skipRefresh = false) =>
  async dispatch => {
    try {
      const pairedUser = await dispatch(fetchUser(skipRefresh));

      if (pairedUser) {
        dispatch(fetchUserSuccess(pairedUser));
        dispatch(refreshCustomizedConfig(pairedUser));
        setAttributes({ userId: pairedUser.uid });
        sendEvent({ key: 'pairingSuccess' });
      }

      return pairedUser;
    } catch (error) {
      dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.PAIRING, error.message));
      sendEvent({ key: 'pairingFail' });

      return null;
    }
  };

export const getStatus = () => async dispatch => {
  try {
    const { status, slowDown } = await dispatch(getProviderPairingStatus());

    if (isPaired(status)) {
      await dispatch(getCurrentPairedUser());
    } else if (slowDown) {
      dispatch(incrementPollingInterval());
    }

    dispatch(fetchStatusSuccess(status));
  } catch (error) {
    dispatch(fetchStatusFailure());
    dispatch(errorAction(getErrorLevel(error), ERROR_TYPES.PAIRING, error.message));
  }
};

export const logout = () => dispatch => dispatch(logoutBase()).finally(() => dispatch(resetSearch()));
